import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Play, FileText, ExternalLink, X, Newspaper, Star } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

// Extraer ID de YouTube desde URL
const getYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

const TYPE_META = {
  nota: { label: "Nota", icon: Newspaper, tone: "text-[#0A2463]" },
  video: { label: "Video", icon: Play, tone: "text-red-500" },
  pdf: { label: "PDF", icon: FileText, tone: "text-amber-600" },
};

const Card = ({ item, onOpenVideo }) => {
  const meta = TYPE_META[item.tipo] || TYPE_META.nota;
  const Icon = meta.icon;
  const ytId = item.tipo === "video" ? getYouTubeId(item.url) : null;
  const thumb =
    item.cover_image_url ||
    (ytId ? `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg` : null);

  const handleClick = (e) => {
    if (item.tipo === "video" && ytId) {
      e.preventDefault();
      onOpenVideo(ytId, item);
    }
  };

  return (
    <motion.article
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      className="group bg-white border border-gray-200 hover:border-[#F5B400] hover:shadow-lg overflow-hidden flex flex-col"
      data-testid={`en-los-medios-card-${item.id}`}
    >
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className="block relative"
        aria-label={item.titulo}
      >
        <div className="relative aspect-video bg-[#0A192F] overflow-hidden">
          {thumb ? (
            <img
              src={thumb}
              alt=""
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Icon size={48} className="text-white/30" />
            </div>
          )}
          {item.tipo === "video" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
              <div className="bg-white/90 group-hover:bg-[#F5B400] w-14 h-14 rounded-full flex items-center justify-center transition-colors">
                <Play size={20} className="text-[#0A192F] ml-1" fill="currentColor" />
              </div>
            </div>
          )}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] font-headings bg-white/95 text-[#0A192F] px-2.5 py-1">
              <Icon size={10} className={`inline mr-1 ${meta.tone}`} /> {meta.label}
            </span>
            {item.destacado && (
              <span className="text-[10px] uppercase tracking-[0.2em] font-headings bg-[#F5B400] text-[#0A192F] px-2.5 py-1 inline-flex items-center gap-1">
                <Star size={10} fill="currentColor" /> Destacado
              </span>
            )}
          </div>
        </div>
      </a>
      <div className="p-5 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.2em] font-headings text-[#F5B400] mb-2">
          {item.medio} · {new Date(item.fecha).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" })}
        </div>
        <h3 className="font-headings text-lg leading-tight text-[#0A192F] mb-2 line-clamp-3">
          {item.titulo}
        </h3>
        {item.descripcion && (
          <p className="text-sm text-gray-600 leading-relaxed mb-3 line-clamp-2 flex-1">
            {item.descripcion}
          </p>
        )}
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="mt-auto text-xs uppercase tracking-[0.15em] font-headings text-[#0A2463] hover:text-[#F5B400] inline-flex items-center gap-1.5"
          data-testid={`en-los-medios-open-${item.id}`}
        >
          {item.tipo === "video" ? "Ver video" : item.tipo === "pdf" ? "Abrir PDF" : "Leer nota"}
          <ExternalLink size={12} />
        </a>
      </div>
    </motion.article>
  );
};

const VideoModal = ({ ytId, item, onClose }) => (
  <div
    className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
    onClick={onClose}
    data-testid="en-los-medios-video-modal"
  >
    <button onClick={onClose} className="absolute top-6 right-6 text-white hover:text-[#F5B400] z-10">
      <X size={32} />
    </button>
    <div className="w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
      <iframe
        src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
        title={item?.titulo || "Video"}
        className="w-full h-full"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  </div>
);

const PAGE_SIZE = 6;

export const EnLosMedios = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [videoOpen, setVideoOpen] = useState(null);

  useEffect(() => {
    axios.get(`${API}/press-coverage`)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((i) => i.tipo === filter);
  }, [items, filter]);

  const shown = filtered.slice(0, visible);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <section id="en-los-medios" className="relative bg-[#F7F8FA] py-20 md:py-28" data-testid="en-los-medios-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="mb-10">
          <div className="inline-flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#0A2463]" />
            <span className="font-headings text-xs tracking-[0.3em] uppercase text-[#0A2463]">
              Prensa
            </span>
          </div>
          <h2 className="font-headings font-bold uppercase tracking-tight text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            En los <span className="fy-underline">medios</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#4B5563] max-w-2xl leading-relaxed">
            Notas, entrevistas y coberturas sobre Frente Ya y Claudio Nazar en los principales medios.
          </p>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap gap-2 mb-8" role="tablist">
          {[
            { id: "all", label: "Todos" },
            { id: "nota", label: "Notas" },
            { id: "video", label: "Videos" },
            { id: "pdf", label: "PDFs" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => { setFilter(f.id); setVisible(PAGE_SIZE); }}
              className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-headings transition-colors border ${
                filter === f.id
                  ? "bg-[#0A192F] text-white border-[#0A192F]"
                  : "bg-white text-[#0A192F] border-gray-300 hover:border-[#0A192F]"
              }`}
              data-testid={`en-los-medios-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <p className="text-sm text-gray-500">Todavía no hay {filter === "all" ? "coberturas" : filter + "s"} publicadas.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {shown.map((item) => (
              <Card key={item.id} item={item} onOpenVideo={(id) => setVideoOpen({ ytId: id, item })} />
            ))}
          </div>
        )}

        {visible < filtered.length && (
          <div className="mt-10 text-center">
            <button
              onClick={() => setVisible(visible + PAGE_SIZE)}
              className="bg-[#0A2463] hover:bg-[#0A192F] text-white font-headings uppercase tracking-wide text-sm px-8 py-3 transition-colors"
              data-testid="en-los-medios-load-more"
            >
              Ver más ({filtered.length - visible})
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {videoOpen && (
          <VideoModal ytId={videoOpen.ytId} item={videoOpen.item} onClose={() => setVideoOpen(null)} />
        )}
      </AnimatePresence>
    </section>
  );
};

export default EnLosMedios;
